/**
 * RS-2 — Questions on the party's own link.
 *
 *   /site-office/:token/questions
 *
 * The contractor asks here and the firm receives an RFI on its RFIs &
 * Submittals page; the answer comes back to this page and by email. A GC also
 * reads the questions of the subs he invited, with the sub's name on the row.
 *
 * Rows come from GET /api/site-office-links/{token}/questions. Files go up the
 * way the Messages card sends them, then their ids ride on the question.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { cacheBranding } from '@/utils/skin';
import { SiteOfficeHead } from '../SiteOfficeSpecs';
import OfficeStrip, { parsePreview } from '@/components/site-office/OfficeStrip';
import { SwatchStrip, attachmentFace, previewSrc } from '@/components/AttachmentChipParts';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

export const shortDate = (value) => (value
  ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  : '');

export const stateWord = (state) => (
  state === 'answered' ? 'Answered' : state === 'closed' ? 'Closed' : 'Waiting on them');

export const stateTone = (state) => (
  state === 'answered' ? 'ok' : state === 'closed' ? '' : 'late');

/** RS-3a — a palette or a board on a question: the picture, and the view-only
 *  link the party can open with no login. */
export const QuestionAttachment = ({ att }) => {
  const face = attachmentFace(att);
  const picture = previewSrc(att);
  if (att.kind !== 'palette' && att.kind !== 'board') return null;
  return (
    <a
      className="kso-att"
      href={att.share_url || picture}
      target="_blank"
      rel="noreferrer"
      data-testid={`office-question-attachment-${att.id}`}
    >
      {att.kind === 'palette'
        ? <SwatchStrip swatches={att.swatches} testId={`office-question-swatches-${att.id}`} />
        : <img src={picture} alt={face.title} className="kso-att-thumb" />}
      <span className="w">
        <b>{face.title}</b>
        <small>{face.micro}</small>
      </span>
    </a>
  );
};

// RS-9 — who wrote each line: the firm, the GC, or the vendor who was asked.
export const roleWord = (line, row) => (
  line.role === 'firm' ? 'Firm'
    : line.role === 'gc' ? 'GC'
      : line.role === 'vendor' ? 'Vendor'
        : line.who || row.from_name || 'A party');

/** One question: what was asked, what it was about, and the answer when it comes. */
export const QuestionRow = ({ row, onReply, replying, text, onText, error, locked, lockedTitle }) => (
  <div className="kso-row" data-testid={`office-question-${row.id}`}>
    <div className="t" style={{ display: 'block' }}>
      <b>{row.subject}</b>
      <small>
        {[row.rfi_number, row.from_name, row.about?.label,
          row.asked_at ? `asked ${shortDate(row.asked_at)}` : ''].filter(Boolean).join(' · ')}
      </small>
      {(row.attachments || []).some((a) => a.kind === 'palette' || a.kind === 'board') ? (
        <span className="kso-atts" data-testid={`office-question-attachments-${row.id}`}>
          {(row.attachments || []).map((a) => <QuestionAttachment key={a.id} att={a} />)}
        </span>
      ) : null}
      {(row.thread || []).length ? (
        <span className="kso-thread" data-testid={`office-question-thread-${row.id}`}>
          {row.thread.map((line, i) => (
            <small key={`${row.id}-line-${i}`}
              data-testid={`office-question-line-${row.id}-${i}`}>
              <b>{roleWord(line, row)}</b>
              {` ${line.text}`}
              {line.created_at ? ` · ${shortDate(line.created_at)}` : ''}
            </small>
          ))}
        </span>
      ) : null}
      {row.answer ? (
        <small data-testid={`office-question-answer-${row.id}`}>
          {`Answer${row.answered_at ? ` ${shortDate(row.answered_at)}` : ''} — ${row.answer}`}
        </small>
      ) : null}
      {row.can_reply && onReply ? (
        <span className="kso-reply" data-testid={`office-question-reply-${row.id}`}>
          <input className="kso-in" value={text || ''}
            placeholder={`Answer for ${row.from_name || 'your sub'}`}
            onChange={(e) => onText(row.id, e.target.value)}
            data-testid={`office-question-reply-text-${row.id}`} />
          <button type="button" className="kso-btn brand" disabled={replying || Boolean(locked)}
            title={lockedTitle}
            onClick={() => onReply(row)}
            data-testid={`office-question-reply-send-${row.id}`}>
            {replying ? 'Sending…' : 'Send'}
          </button>
          {error ? (
            <small className="kso-hint kso-hint--late"
              data-testid={`office-question-reply-error-${row.id}`}>{error}</small>
          ) : null}
        </span>
      ) : null}
    </div>
    <span className={`kso-tag ${stateTone(row.state)}`} data-testid={`office-question-state-${row.id}`}>
      {stateWord(row.state)}
    </span>
  </div>
);

export default function OfficeQuestions() {
  const { token } = useParams();
  const navigate = useNavigate();
  // SO-9a — the firm reading this page from the preview.
  const [params] = useSearchParams();
  const preview = parsePreview(params.get('preview'));
  const [office, setOffice] = useState(null);
  const [data, setData] = useState(null);
  const [gone, setGone] = useState('');
  const [question, setQuestion] = useState('');
  const [details, setDetails] = useState('');
  const [about, setAbout] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // RS-9 — the GC's own answer to his sub, one line per question.
  const [replies, setReplies] = useState({});
  const [replyingTo, setReplyingTo] = useState('');
  const [replyError, setReplyError] = useState({});

  const load = useCallback(async () => {
    try {
      const [head, list] = await Promise.all([
        bare.get(`${API}/site-office-links/${token}`),
        bare.get(`${API}/site-office-links/${token}/questions`),
      ]);
      setOffice(head.data);
      if (head.data?.branding) cacheBranding(head.data.branding);
      setData(list.data);
    } catch (_e) { setGone('This link is not valid'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const reply = async (row) => {
    const words = (replies[row.id] || '').trim();
    if (!words) {
      setReplyError({ ...replyError, [row.id]: 'Write the answer first' });
      return;
    }
    setReplyingTo(row.id);
    setReplyError({ ...replyError, [row.id]: '' });
    try {
      const sent = await bare.post(
        `${API}/site-office-links/${token}/questions/${row.id}/reply`, { text: words });
      setData({
        ...data,
        questions: (data.questions || []).map((q) => (q.id === row.id ? sent.data : q)),
      });
      setReplies({ ...replies, [row.id]: '' });
    } catch (e) {
      setReplyError({ ...replyError,
        [row.id]: e?.response?.data?.detail || 'That did not send. Try again.' });
    }
    setReplyingTo('');
  };

  const pick = (e) => {
    setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 5));
    e.target.value = '';
  };

  const send = async () => {
    const words = question.trim();
    if (!words) { setError('Write the question first'); return; }
    setSending(true);
    setError('');
    try {
      let attachmentIds = [];
      if (files.length) {
        const form = new FormData();
        form.append('text', `Files for a question: ${words.slice(0, 70)}`);
        files.forEach((f) => form.append('files', f));
        const up = await bare.post(`${API}/site-office-links/${token}/messages/with-files`, form);
        attachmentIds = (up.data?.attachments || []).map((a) => a.id);
      }
      const [kind, id] = about ? about.split(':') : [];
      const body = {
        question: details.trim() ? `${words}\n\n${details.trim()}` : words,
        attachment_ids: attachmentIds,
      };
      if (kind && id) body.about = { kind, id };
      const made = await bare.post(`${API}/site-office-links/${token}/questions`, body);
      setData({ ...data, questions: [made.data, ...(data.questions || [])] });
      setQuestion('');
      setDetails('');
      setAbout('');
      setFiles([]);
    } catch (e) {
      setError(e?.response?.data?.detail || 'That did not send. Try again.');
    }
    setSending(false);
  };

  if (gone) {
    return (
      <main className="kso-page" data-testid="office-questions">
        <div className="kso-wrap"><div className="kso-gate"><h1>{gone}</h1></div></div>
      </main>
    );
  }
  if (!office || !data) {
    return (
      <main className="kso-page" data-testid="office-questions">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const rows = data.questions || [];
  const options = data.about_options || {};
  const waiting = rows.filter((r) => r.state === 'waiting_on_them').length;

  return (
    <main className="kso-page" data-testid="office-questions">
      <div className="kso-wrap">
        {preview ? (
          <OfficeStrip projectId={preview.projectId} membershipId={preview.membershipId}
            projectName={office.project?.name} partyName={office.party?.name} page="Questions" />
        ) : null}
        <SiteOfficeHead data={office} />
        <div className="kso-crumb" data-testid="office-questions-crumb">
          <button type="button" className="back" data-testid="office-questions-back"
            onClick={() => navigate(preview ? `/projects/${preview.projectId}/site-office/${preview.membershipId}?view=party` : `/site-office/${token}`)}>‹ Site Office</button>
          <span>Site Office</span><span>›</span><b>Questions</b>
        </div>
        <div className="kso-grid">
          <section className="kso-card w12" data-testid="office-questions-card">
            <div className="kso-card__hd">
              Questions
              <span className="n">
                {rows.length ? `${waiting} waiting on them` : 'none yet'}
              </span>
            </div>
            <div className="kso-card__bd">
              <p className="kso-hint" style={{ margin: '0 0 var(--km-s2)' }}>
                {`Ask ${office.firm_name || 'the firm'} anything about this job. `}
                Answers land here and in your email.
              </p>
              {rows.length === 0 ? (
                <p className="kso-hint" style={{ margin: 0 }} data-testid="office-questions-empty">
                  Nothing asked yet. Ask your first question below.
                </p>
              ) : rows.map((row) => (
                <QuestionRow key={row.id} row={row} onReply={reply}
                  replying={replyingTo === row.id}
                  text={replies[row.id]}
                  onText={(id, value) => setReplies({ ...replies, [id]: value })}
                  error={replyError[row.id]}
                  locked={Boolean(preview)}
                  lockedTitle={preview ? `Only ${office.party?.name} can do this` : undefined} />
              ))}
            </div>
          </section>
          <section className="kso-card w12" data-testid="office-ask">
            <div className="kso-card__hd">Ask a question</div>
            <div className="kso-card__bd">
              <div className="kso-form">
                <input className="kso-in" placeholder="What do you need to know?"
                  value={question} onChange={(e) => setQuestion(e.target.value)}
                  data-testid="office-ask-question" />
                <select className="kso-in" value={about} onChange={(e) => setAbout(e.target.value)}
                  data-testid="office-ask-about">
                  <option value="">About… (optional)</option>
                  {(options.sheets || []).map((o) => (
                    <option key={`sheet:${o.id}`} value={`sheet:${o.id}`}>{`Sheet · ${o.label}`}</option>
                  ))}
                  {(options.specs || []).map((o) => (
                    <option key={`spec:${o.id}`} value={`spec:${o.id}`}>{`Spec · ${o.label}`}</option>
                  ))}
                  {(options.punch || []).map((o) => (
                    <option key={`punch:${o.id}`} value={`punch:${o.id}`}>{`Punch · ${o.label}`}</option>
                  ))}
                </select>
                <textarea className="kso-in" rows={3}
                  placeholder="Details, measurements, what you saw"
                  value={details} onChange={(e) => setDetails(e.target.value)}
                  data-testid="office-ask-details" />
                {files.length ? (
                  <div data-testid="office-ask-files">
                    {files.map((f) => (
                      <span className="kso-tag" key={f.name}>
                        {f.name}
                        <button type="button" className="x" aria-label="Remove"
                          onClick={() => setFiles(files.filter((x) => x !== f))}>×</button>
                      </span>
                    ))}
                  </div>
                ) : null}
                <label className="kso-btn" data-testid="office-ask-attach">
                  Add a photo or a file
                  <input type="file" accept="image/*,application/pdf" multiple hidden
                    onChange={pick} />
                </label>
                {error ? (
                  <p className="kso-hint kso-hint--late" data-testid="office-ask-error">{error}</p>
                ) : null}
                <button type="button" className="kso-btn brand" disabled={sending || Boolean(preview)}
                  title={preview ? `Only ${office.party?.name} can do this` : undefined}
                  onClick={send} data-testid="office-ask-send">
                  {sending ? 'Sending…' : 'Send the question'}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
